import { MailIcon, PlusCircleIcon, type LucideIcon } from "lucide-react"
import { useState, useEffect } from "react"

import { Button } from "@/components/ui/button"
import { useComingSoon } from "./coming-soon-modal"
import {
  SidebarGroup,
  SidebarGroupContent,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from "@/components/ui/sidebar"

export function NavMain({
  items,
  currentPath,
}: {
  items: {
    title: string
    url: string
    icon?: LucideIcon
    onClick?: () => void
  }[]
  currentPath?: string
}) {
  const { showComingSoon } = useComingSoon();
  const [activeSection, setActiveSection] = useState<string>('');

  // Section mapping for scroll tracking
  const sectionMap: { [key: string]: string } = {
    'Overview': 'overview',
    'Dashboard': 'dashboard',
    'Product Analysis': 'product-analysis', 
    'Market Intelligence': 'market-intelligence',
    'Supply Chain': 'supply-chain',
    'Sustainability': 'sustainability',
    'Product Recommendations': 'product-recommendations'
  };

  // Scroll tracking effect
  useEffect(() => {
    // Only enable scroll tracking on individual dashboard pages (analysis reports)
    const isDashboardPage = currentPath?.startsWith('/dashboard/');
    
    if (!isDashboardPage) {
      // Reset active section when not on a dashboard page
      setActiveSection('');
      return;
    }

    const handleScroll = () => {
      const sections = Object.values(sectionMap);
      const scrollPosition = window.scrollY + 150; // Adjust for better detection
      
      let currentSection = '';
      
      // Find which section is currently most visible
      for (let i = sections.length - 1; i >= 0; i--) {
        const sectionId = sections[i];
        const element = document.getElementById(sectionId);
        if (element) {
          const elementTop = element.offsetTop;
          
          if (scrollPosition >= elementTop) {
            currentSection = sectionId;
            break;
          }
        }
      }
      
      // Default to overview if no section is detected (user is at the top)
      if (!currentSection) {
        currentSection = 'overview';
      }
      
      // Update active section if it changed
      if (currentSection) {
        setActiveSection(currentSection);
      }
    };

    console.log('Setting up scroll listener for dashboard page');

    // Add scroll listener with throttling for better performance
    let ticking = false;
    const throttledHandleScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          handleScroll();
          ticking = false;
        });
        ticking = true;
      }
    };

    // Set initial active section to overview
    setActiveSection('overview');

    // Add scroll listener immediately
    window.addEventListener('scroll', throttledHandleScroll, { passive: true });
    
    // Check initial position after a short delay to ensure DOM is ready
    setTimeout(() => {
      handleScroll();
    }, 100);
    
    return () => {
      console.log('Cleaning up scroll listener');
      window.removeEventListener('scroll', throttledHandleScroll);
    };
  }, [currentPath]); // Only depend on currentPath, not activeSection

  const handleNavClick = (sectionId: string) => {
    if (sectionId === 'overview') {
      // For overview, scroll to the very top of the page
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
      setActiveSection('overview');
    } else {
      const element = document.getElementById(sectionId);
      if (element) {
        const headerHeight = 80; // Account for fixed header
        const elementPosition = element.offsetTop - headerHeight;
        window.scrollTo({
          top: elementPosition,
          behavior: 'smooth'
        });
        setActiveSection(sectionId); // Immediately update active section
      }
    }
  };

  const isActive = (url: string, title: string) => {
    if (!currentPath) return false;
    
    // Only highlight dashboard sections when on individual dashboard pages
    if (url === '#' || url === '#overview') {
      const isDashboardPage = currentPath?.startsWith('/dashboard/');
      if (!isDashboardPage) return false;
      
      const sectionId = sectionMap[title];
      return activeSection === sectionId;
    }
    
    // Special case for dashboard -> reports redirect
    if (url === '/reports' && (currentPath === '/reports' || currentPath === '/')) {
      return true;
    }
    return currentPath === url;
  };

  return (
    <SidebarGroup>
      <SidebarGroupContent className="flex flex-col gap-2">
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton
              tooltip="Quick Create"
              className="bg-primary text-primary-foreground duration-200 ease-linear hover:bg-primary/90 hover:text-primary-foreground active:bg-primary/90 active:text-primary-foreground cursor-pointer"
              asChild
            >
              <a href="/" className="flex items-center gap-2 w-full">
                <PlusCircleIcon />
                <span>Quick Create</span>
              </a>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
        <SidebarMenu>
          {items.map((item) => (
            <SidebarMenuItem key={item.title}>
              <SidebarMenuButton 
                tooltip={item.title}
                asChild={item.url !== '#' && item.url !== '#overview' && !item.onClick}
                onClick={() => {
                  if (item.onClick) {
                    item.onClick();
                  } else if (item.url === '#' || item.url === '#overview') {
                    // Extract section ID from title for smooth scrolling
                    const sectionId = sectionMap[item.title];
                    if (sectionId) {
                      handleNavClick(sectionId);
                    }
                  }
                }}
                className={`cursor-pointer hover:bg-accent hover:text-accent-foreground transition-all duration-200 ${
                  isActive(item.url, item.title) 
                    ? 'bg-accent text-accent-foreground border-l-4 border-primary shadow-sm font-medium' 
                    : 'border-l-4 border-transparent'
                }`}
              >
                {item.url !== '#' && item.url !== '#overview' && !item.onClick ? (
                  <a href={item.url} className="flex items-center gap-2 w-full">
                    {item.icon && <item.icon />}
                    <span>{item.title}</span>
                  </a>
                ) : (
                  <>
                    {item.icon && <item.icon />}
                    <span className="flex items-center gap-2">
                      {item.title}
                      {isActive(item.url, item.title) && (
                        <div className="w-2 h-2 rounded-full bg-primary animate-pulse"></div>
                      )}
                    </span>
                  </>
                )}
              </SidebarMenuButton>
            </SidebarMenuItem>
          ))}
        </SidebarMenu>
      </SidebarGroupContent>
    </SidebarGroup>
  )
}
